import { useMutation, useQueryClient } from '@tanstack/react-query';

import API from '@api/API';

import { SplashNotification, useCurrentUser } from '@queries/UserQueries';
import { useCurrentUserScheduleSubmissionQuery } from '@queries/UserScheduleSubmissionsQueries';

interface UpdateUserScheduleSubmissionPayload {
  timezone: string;
  dates: string[];
}

export const useUpdateUserScheduleSubmissionMutation = () => {
  const { currentUser } = useCurrentUser();
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: (payload: UpdateUserScheduleSubmissionPayload) => {
      return API.post<
        { splashNotification?: SplashNotification },
        UpdateUserScheduleSubmissionPayload
      >(`/v1/user/schedule-submissions`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: useCurrentUserScheduleSubmissionQuery.queryKey(currentUser?._id ?? ''),
      });
    },
  });

  return {
    isSubmitting,
    updateUserScheduleSubmissionAsync: mutateAsync,
  };
};

interface CreateScheduleDateDetailsPayload {
  date: string;
  startTime?: string;
  showName: string;
  venue: string;
  entertainerIds?: string[];
  promoterIds?: string[];
  castSize?: number;
}

export const useCreateScheduleDateDetailsMutation = () => {
  const { currentUser } = useCurrentUser();
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: (payload: CreateScheduleDateDetailsPayload) => {
      return API.post<{}, CreateScheduleDateDetailsPayload>(
        `/v1/user/schedule-submissions/date-details`,
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: useCurrentUserScheduleSubmissionQuery.queryKey(currentUser?._id ?? ''),
      });
    },
  });

  return {
    isSubmitting,
    createScheduleDateDetailsAsync: mutateAsync,
  };
};

interface UpdateScheduleDateDetailsPayload {
  img?: any;
  startTime?: string;
  scheduleDateDetailsId: string;
  showName?: string;
  venue?: string;
  entertainerIds?: string[];
  promoterIds?: string[];
  castSize?: number;
}

export const useUpdateScheduleDateDetailsMutation = () => {
  const { currentUser } = useCurrentUser();
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: ({ scheduleDateDetailsId, ...payload }: UpdateScheduleDateDetailsPayload) => {
      return API.post<{}, Omit<UpdateScheduleDateDetailsPayload, 'scheduleDateDetailsId'>>(
        `/v1/user/schedule-submissions/date-details/${scheduleDateDetailsId}`,
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: useCurrentUserScheduleSubmissionQuery.queryKey(currentUser?._id ?? ''),
      });
    },
  });

  return {
    isSubmitting,
    updateScheduleDateDetailsAsync: mutateAsync,
  };
};

interface UpdatePendingRecurringEventPayload {
  eventId: string;
  status?: 'approved' | 'rejected' | 'ended';
  flyer?: {
    base64Image?: string | ArrayBuffer;
    previousEventId?: string;
  };
}

export const useUpdatePendingRecurringEventMutation = () => {
  const { currentUser } = useCurrentUser();
  const queryClient = useQueryClient();

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: ({ eventId, ...payload }: UpdatePendingRecurringEventPayload) => {
      return API.post<{}, Omit<UpdatePendingRecurringEventPayload, 'eventId'>>(
        `/v1/user/schedule-submissions/pending-recurring-events/${eventId}`,
        payload,
      );
    },
  });

  return {
    isSubmitting,
    updatePendingRecurringEventAsync: mutateAsync,
  };
};

interface CreateScheduleDateDetailsEventMutationPayload {
  title: string;
  startDate: string;
  startTime: string;
  cityId: string;
  entertainerIds?: string[];
  tagIds?: string[];
}

export const useCreateScheduleDateDetailsEventMutation = () => {
  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: async ({
      scheduleDateDetailsId,
      eventImage,
      ...payload
    }: CreateScheduleDateDetailsEventMutationPayload & {
      scheduleDateDetailsId: string;
      eventImage?: string | ArrayBuffer;
    }) => {
      const { event } = await API.post<
        { event: { _id: string } },
        CreateScheduleDateDetailsEventMutationPayload
      >(`/v1/user/schedule-submissions/date-details/${scheduleDateDetailsId}/events`, payload);

      if (eventImage) {
        await API.put(`/v1/events/${event._id}`, { eventImage });
      }

      return { event };
    },
  });

  return {
    isSubmitting,
    createScheduleDateDetailsEventAsync: mutateAsync,
  };
};

export const useReprocessScheduleDateDetailsMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ scheduleDateDetailsIds }: { scheduleDateDetailsIds: (string | undefined)[] }) =>
      API.post<{ queuedCount: number }, { scheduleDateDetailsIds: string[] }>(
        '/v1/user/schedule-submissions/reprocess-date-details',
        { scheduleDateDetailsIds: scheduleDateDetailsIds.filter((id): id is string => !!id) },
      ),
  });

  return {
    isReprocessingScheduleDateDetails: isPending,
    reprocessScheduleDateDetailsAsync: mutateAsync,
  };
};
